import { useCallback } from 'react';
import { useUnderwayStore, buildGroupKey } from './useUnderwayStore';
import type { UnderwayActivityDetails, UnderwayCandidate } from './types';

// Inputs callers provide to enqueue a delayed trigger into the
// popup. The move itself has already happened — these executions
// were created by BE in the move's wake and are sitting in `pending`
// state, waiting for the FE to call `/trigger` (or `/cancel`).
export type EnqueueAutomationMoveInput = {
  // Activity (rule action) details — drives icon + title in the row.
  activityKind: string;
  activityLabel: string;
  // Group key inputs: same stage + same activityKey collapse.
  // activityKey defaults to activityKind+activityLabel.
  activityKey?: string;
  stageId: string | number;
  stageName: string;
  jobId: string | number;
  jobName: string;
  candidates: UnderwayCandidate[];
  isBulk: boolean;
  executionIds: number[];
  // Kind-specific copy context (interview mode, survey audience, recipients).
  details?: UnderwayActivityDetails;
  // Move-initiation time for ordering. Bulk callers pass the dispatch
  // session's `startedAt` (the move happened before the poller resolved);
  // immediate callers omit it and the store stamps "now" at enqueue.
  initiatedAt?: number;
  // First step is BE-delayed → the row's copy reads "scheduled" / "Sends in X"
  // (see `UnderwayActivity`). No effect on the timer/trigger.
  delayed?: boolean;
  delaySeconds?: number;
};

// Returns a stable enqueue callback used by `useMoveStageWith
// AutomationConfirm`. Callers shouldn't need this directly — the
// confirm hook owns the move→fetch-executions→enqueue chain.
export const useEnqueueAutomationMove = () => {
  const enqueue = useUnderwayStore((s) => s.enqueue);

  return useCallback(
    (input: EnqueueAutomationMoveInput) => {
      const activityKey =
        input.activityKey ?? `${input.activityKind}:${input.activityLabel}`;
      // Bulk rows collapse by stage+activity; single moves stay
      // individual line items (keyed by execution ids).
      const groupSuffix = input.isBulk
        ? activityKey
        : `${activityKey}:${input.executionIds.join('-')}`;
      const groupKey = buildGroupKey(String(input.stageId), groupSuffix);
      enqueue({
        groupKey,
        activityKind: input.activityKind,
        activityLabel: input.activityLabel,
        stageId: String(input.stageId),
        stageName: input.stageName,
        jobId: String(input.jobId),
        jobName: input.jobName,
        candidates: input.candidates,
        isBulk: input.isBulk,
        executionIds: input.executionIds,
        details: input.details,
        initiatedAt: input.initiatedAt,
        delayed: input.delayed,
        delaySeconds: input.delaySeconds,
      });
      // Signal the shared activities popup (GlobalBulkOperationQueue) to
      // open expanded, mirroring the `bulkActionStarted` event used by bulk
      // actions. Kept as a DOM event so the automation feature stays
      // decoupled from the popup's internals.
      window.dispatchEvent(new Event('automationEnqueued'));
    },
    [enqueue]
  );
};
